import React, { useState, useRef } from "react";
import { Icon } from "@iconify/react";

// Import feature sections
import FeatureSectionRecording from "../../components/landing/features/FeatureSectionRecording";
import FeatureSectionPDF from "../../components/landing/features/FeatureSectionPDF";
import FeatureSectionAIMemory from "../../components/landing/features/FeatureSectionAIMemory";
import FeatureSectionStudyTools from "../../components/landing/features/FeatureSectionStudyTools";
import FeatureSectionYouTube from "../../components/landing/features/FeatureSectionYouTube";

type ViewMode = "desktop" | "mobile" | "card-only";
type FeatureCard = "recording" | "pdf" | "ai-memory" | "study-tools" | "youtube";

const featureCards: { id: FeatureCard; name: string; icon: string }[] = [
  { id: "recording", name: "Recording", icon: "lucide:mic" },
  { id: "pdf", name: "PDF Import", icon: "lucide:file-text" },
  { id: "ai-memory", name: "AI Memory", icon: "lucide:brain" },
  { id: "study-tools", name: "Study Tools", icon: "lucide:graduation-cap" },
  { id: "youtube", name: "YouTube", icon: "lucide:youtube" },
];

const LandingCardRecorder: React.FC = () => {
  const [selectedCard, setSelectedCard] = useState<FeatureCard>("recording");
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [isRecording, setIsRecording] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Viewport dimensions based on view mode
  // Desktop: full width (1200px) - description + card side by side
  // Mobile: narrow width (480px) - triggers responsive layout (description top, card bottom)
  // Card Only: just the card, no description
  const getViewportStyle = () => {
    switch (viewMode) {
      case "mobile":
        return { width: 480, maxWidth: "100%" };
      case "card-only":
        return { width: "auto", maxWidth: "100%" };
      case "desktop":
      default:
        return { width: 1200, maxWidth: "100%" };
    }
  };

  // Determine if we should show description
  const shouldShowDescription = viewMode !== "card-only";

  // Render the selected feature card with showDescription prop
  const renderFeatureCard = () => {
    const props = { showDescription: shouldShowDescription };

    switch (selectedCard) {
      case "recording":
        return <FeatureSectionRecording {...props} />;
      case "pdf":
        return <FeatureSectionPDF {...props} />;
      case "ai-memory":
        return <FeatureSectionAIMemory {...props} />;
      case "study-tools":
        return <FeatureSectionStudyTools {...props} />;
      case "youtube":
        return <FeatureSectionYouTube {...props} />;
      default:
        return null;
    }
  };

  const handleStartRecording = () => {
    setIsRecording(true);
    // Note: This button is a placeholder. For actual recording:
    // - Use screen recording software (Loom, OBS, QuickTime)
    // - Or use browser DevTools to capture node screenshots
  };

  const handleStopRecording = () => {
    setIsRecording(false);
  };

  const handleScreenshot = async () => {
    if (!cardRef.current) return;
    alert("Right-click the card preview and select 'Capture node screenshot' in DevTools, or use a screen recording tool like Loom, OBS, or QuickTime.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-slate-900/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Landing Page Card Recorder</h1>
              <p className="text-slate-400 text-sm">Preview & record feature cards in different views</p>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              {/* View Mode */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">View:</span>
                <div className="flex bg-slate-800 rounded-lg p-1">
                  {[
                    { id: "desktop", icon: "lucide:monitor", label: "Desktop", desc: "Side by side" },
                    { id: "mobile", icon: "lucide:smartphone", label: "Mobile", desc: "Stacked" },
                    { id: "card-only", icon: "lucide:square", label: "Card Only", desc: "No text" },
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setViewMode(mode.id as ViewMode)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                        viewMode === mode.id
                          ? "bg-blue-500 text-white"
                          : "text-slate-400 hover:text-white"
                      }`}
                      title={mode.desc}
                    >
                      <Icon icon={mode.icon} />
                      <span className="hidden sm:inline">{mode.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Record Button - placeholder for external tools */}
              <button
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  isRecording
                    ? "bg-red-500 text-white animate-pulse"
                    : "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                }`}
              >
                <Icon icon={isRecording ? "lucide:square" : "lucide:circle"} className="text-lg" />
                {isRecording ? "Stop" : "Record"}
              </button>

              {/* Screenshot Button */}
              <button
                onClick={handleScreenshot}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-all"
              >
                <Icon icon="lucide:camera" />
                Screenshot
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Card Selection Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {featureCards.map((card) => (
            <button
              key={card.id}
              onClick={() => setSelectedCard(card.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedCard === card.id
                  ? "bg-white text-slate-900 shadow-lg"
                  : "bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 hover:text-white"
              }`}
            >
              <Icon icon={card.icon} />
              {card.name}
            </button>
          ))}
        </div>

        {/* View Mode Description */}
        <div className="mb-4 text-sm text-slate-500">
          {viewMode === "desktop" && "Desktop view: Description on left/right, card on the other side"}
          {viewMode === "mobile" && "Mobile view: Description on top, card on bottom (responsive layout)"}
          {viewMode === "card-only" && "Card only: Just the animated card, no description text"}
        </div>

        {/* Preview Container */}
        <div className="flex justify-center overflow-x-auto pb-4">
          <div
            ref={cardRef}
            className="bg-white rounded-2xl overflow-hidden transition-all duration-300"
            style={{
              ...getViewportStyle(),
              boxShadow: "0 25px 80px -20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)",
            }}
          >
            {renderFeatureCard()}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {/* Export Instructions */}
          <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Icon icon="lucide:download" className="text-blue-400" />
              How to Export
            </h2>
            <div className="space-y-3 text-sm text-slate-300">
              <p className="font-medium text-white">For Screenshots:</p>
              <p className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0">1</span>
                Right-click the card preview above
              </p>
              <p className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0">2</span>
                Select "Inspect" to open DevTools
              </p>
              <p className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0">3</span>
                Right-click the element → "Capture node screenshot"
              </p>
              <p className="font-medium text-white mt-4">For Video Recording:</p>
              <p className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs flex-shrink-0">•</span>
                Use Loom, OBS, QuickTime, or similar screen recording software
              </p>
              <p className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs flex-shrink-0">•</span>
                Record the card animation playing in the browser
              </p>
            </div>
          </div>

          {/* View All Cards */}
          <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Icon icon="lucide:grid" className="text-green-400" />
              Quick Preview All Cards
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {featureCards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCard(card.id)}
                  className={`p-4 rounded-xl text-left transition-all ${
                    selectedCard === card.id
                      ? "bg-blue-500/20 border-blue-500/50 border"
                      : "bg-slate-700/50 hover:bg-slate-700 border border-transparent"
                  }`}
                >
                  <Icon icon={card.icon} className="text-2xl text-slate-300 mb-2" />
                  <p className="text-sm font-medium text-white">{card.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Info about recording */}
        <div className="mt-6 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
          <p className="text-sm text-yellow-200 flex items-start gap-2">
            <Icon icon="lucide:info" className="text-lg flex-shrink-0 mt-0.5" />
            <span>
              <strong>Recording:</strong> The Record button is a visual indicator only.
              Use external screen recording tools (Loom, OBS, QuickTime) to capture the animations.
              Upload recorded videos to your storage (S3, Cloudflare R2, etc.) separately.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingCardRecorder;
